import React from "react"
import { Link } from "gatsby"
import Layout from "../../components/Layout"
import Pagetitle from "../../components/Pagetitle"
import Seo from "../../components/Seo"
import RelatedPlatforms from "../../components/RelatedPlatforms"
import { PLATFORM_GUIDE_OG_IMAGE } from "../../data/platforms"

const MicrosoftAdsPage = () => (
  <Layout>
    <Seo
      title="Shopify Product Feed for Microsoft Ads | UpFeed"
      description="Learn how to add a Shopify product feed to Microsoft Merchant Center with UpFeed, including feed setup, requirements, troubleshooting, and Shopping campaign tips."
      image={PLATFORM_GUIDE_OG_IMAGE}
    />

    <div className="container">
      <div className="row justify-content-center">
        <div className="getting-started col-md-8">
          <Link to="/getting-started/#platforms" className="back-link">
            ← All platforms
          </Link>

          <Pagetitle>
            How to Add a Shopify Product Feed to Microsoft Ads
          </Pagetitle>

          <p>
            You can connect your Shopify store to Microsoft Advertising by
            adding your UpFeed product feed to Microsoft Merchant Center. Once
            connected, Microsoft can use the product information in your feed
            to create Product ads and power Microsoft Shopping campaigns.
            UpFeed gives you a hosted product feed URL that Microsoft Merchant
            Center can automatically download on a recurring schedule.
          </p>

          <h2>What is a Microsoft Ads product feed?</h2>
          <p>
            A Microsoft Ads product feed is a structured file containing the
            products you want to advertise through Microsoft Advertising. The
            feed provides Microsoft Merchant Center with product information
            such as your product IDs, titles, descriptions, prices, images,
            availability, landing page URLs, brands, and categories.
          </p>
          <p>
            Microsoft Advertising uses this catalog data to understand what you
            sell and to create Product ads for eligible Shopping campaigns.
            Instead of creating an individual ad for every item in your store,
            you maintain your product information in the feed and Microsoft can
            use that data when displaying relevant products to shoppers.
          </p>

          <h2>Why connect Shopify to Microsoft Ads with UpFeed?</h2>
          <p>
            Shopify contains the source product data for your store, but
            advertising platforms often expect that data in a specific feed
            structure. UpFeed creates a dedicated product feed from your Shopify
            catalog and gives you tools to control how your store data is sent
            to advertising channels.
          </p>
          <p>
            You can use column mapping to connect Shopify fields to feed
            attributes, category mapping to organize your products, feed
            settings to control the output, and feed rules to adjust product
            values without changing the original products in Shopify. This is
            useful when Microsoft Advertising needs a different title,
            category, description, or other feed value than the one you use on
            your storefront.
          </p>

          <h2>Microsoft Merchant Center product feed requirements</h2>
          <p>
            Microsoft Merchant Center uses your feed to build and validate your
            product catalog. Before submitting the feed, make sure the product
            information is complete and consistent with the corresponding
            product pages on your website. Important product attributes include
            identifiers, titles, descriptions, prices, availability, product
            URLs, image URLs, brands, and product categories.
          </p>
          <p>
            Microsoft Merchant Center supports catalog feeds based on the Google
            product feed format, which makes it easier to reuse structured
            ecommerce product data across Google and Microsoft. UpFeed generates
            a hosted feed that can be submitted using Microsoft Merchant
            Center's automatic URL download option.
          </p>

          <div className="prerequisites">
            <p>Before you start, make sure you have:</p>
            <ul>
              <li>
                <Link to="/getting-started/#generate">
                  Generated your product feed
                </Link>{" "}
                and copied the feed URL from the UpFeed app homepage.
              </li>
              <li>A Microsoft Advertising account.</li>
              <li>
                A Microsoft Merchant Center store associated with your website.
              </li>
              <li>
                Verified or claimed the website domain used by your store where
                required by Microsoft Merchant Center.
              </li>
            </ul>
          </div>

          <h2>How to add your UpFeed product feed to Microsoft Merchant Center</h2>
          <ol>
            <li>
              Log in to{" "}
              <a
                href="https://ads.microsoft.com"
                target="_blank"
                rel="noreferrer"
              >
                Microsoft Advertising
              </a>
              .
            </li>
            <li>
              Open <strong>Tools</strong>, go to{" "}
              <strong>Merchant Center</strong>, and select the Merchant Center
              store you want to use.
            </li>
            <li>
              Open the <strong>Feeds</strong> section and choose{" "}
              <strong>Create feed</strong>.
            </li>
            <li>
              Enter a descriptive feed name and select{" "}
              <strong>Primary feed</strong> as the feed type if this will be
              your main source of online product data.
            </li>
            <li>
              Configure the market, language, destination, and currency settings
              that apply to the products in the feed.
            </li>
            <li>
              For the input method, choose the option to{" "}
              <strong>automatically download the file from a URL</strong>.
            </li>
            <li>
              Paste the product feed URL you copied from UpFeed into the source
              URL field.
            </li>
            <li>
              Configure a recurring download schedule. A daily refresh is a
              practical choice for most ecommerce stores because it helps keep
              prices, availability, and other changing product information
              current.
            </li>
            <li>
              Save the feed and allow Microsoft Merchant Center to download,
              process, and review your products.
            </li>
          </ol>

          <h2>What happens after Microsoft imports your feed?</h2>
          <p>
            After the feed has been processed, review its status in Microsoft
            Merchant Center. Microsoft validates the product data and may flag
            individual offers that contain missing, invalid, or inconsistent
            information. Correct any feed issues before launching campaigns so
            that as many eligible products as possible can be used for Product
            ads.
          </p>
          <p>
            Once your Merchant Center store and product offers are approved, you
            can create a Microsoft Shopping campaign and connect it to the
            relevant Merchant Center store and catalog. The product feed then
            supplies the product information used in your Shopping ads.
          </p>

          <h2>Keeping your Microsoft Ads product feed up to date</h2>
          <p>
            Product feeds should be refreshed regularly because ecommerce data
            changes frequently. Prices may change, products can go in or out of
            stock, new products are added, and product information can be
            updated in Shopify. Using Microsoft Merchant Center's scheduled URL
            download allows Microsoft to fetch a fresh version of the UpFeed
            feed automatically.
          </p>
          <p>
            Microsoft recommends keeping feed information current, and feeds
            that are not updated for an extended period can stop serving. A
            daily scheduled download is therefore a good default for most
            stores.
          </p>

          <h2>Common Microsoft Merchant Center feed problems</h2>
          <p>
            If products are rejected or missing after an import, start by
            checking the feed diagnostics in Microsoft Merchant Center. Common
            causes include missing product information, invalid URLs, problems
            with images, mismatches between the feed and the product landing
            page, or products that do not meet Microsoft Advertising policies.
          </p>
          <p>
            If the feed itself cannot be downloaded, confirm that the UpFeed
            feed URL is correct and publicly accessible. If only specific
            products have issues, use UpFeed's column mapping and feed rules to
            adjust the affected attributes, regenerate the feed, and let
            Microsoft fetch the updated version on the next scheduled import.
          </p>

          <h2>Microsoft Ads product feed FAQ</h2>

          <h3>Can I use a Shopify product feed with Microsoft Advertising?</h3>
          <p>
            Yes. Shopify product data can be provided to Microsoft Merchant
            Center through a compatible product feed. UpFeed creates a hosted
            product feed from your Shopify catalog that can be added to
            Microsoft Merchant Center using an automatically downloaded URL.
          </p>

          <h3>How often should Microsoft Merchant Center update my feed?</h3>
          <p>
            A daily update is a useful default for most ecommerce stores.
            Stores with frequently changing prices or availability should make
            sure their feed refresh schedule keeps Microsoft Merchant Center
            sufficiently up to date.
          </p>

          <h3>Can Microsoft Merchant Center use Google-style product feeds?</h3>
          <p>
            Yes. Microsoft Merchant Center supports Google product feed data,
            which makes it easier to reuse well-structured ecommerce product
            information across multiple advertising platforms.
          </p>

          <h3>Do I need to create a separate ad for every Shopify product?</h3>
          <p>
            No. Microsoft Shopping campaigns use the products in your Merchant
            Center catalog to create Product ads. Your feed supplies the product
            information, while your campaign settings control how eligible
            products are advertised.
          </p>

          <h3>What should I do if Microsoft rejects products from my feed?</h3>
          <p>
            Check the product diagnostics in Microsoft Merchant Center to find
            the specific reason for each issue. Correct the underlying product
            data or use UpFeed's mapping and feed-rule features where
            appropriate, regenerate the feed, and allow Microsoft to process the
            updated data.
          </p>

          <RelatedPlatforms currentSlug="microsoft-ads" />
        </div>
      </div>
    </div>
  </Layout>
)

export default MicrosoftAdsPage